// MultiSelect.js - Multi-select dropdown for sources/types
import React, { useState, useRef, useEffect } from 'react';
import { FiChevronDown, FiCheck, FiX } from 'react-icons/fi';

const MultiSelect = ({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = 'All',
  allLabel = 'All'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAllSelected = value.length === 0 || value.length === options.length;

  const handleToggle = (option) => {
    if (value.includes(option)) {
      onChange(value.filter(v => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  const handleSelectAll = () => {
    onChange([]);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const displayValue = isAllSelected
    ? allLabel
    : value.length === 1
      ? value[0]
      : `${value.length} selected`;

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-xs text-gray-400 mb-1">{label}</label>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded transition-colors min-w-[140px]"
      >
        <span className="text-sm flex-1 text-left truncate">{displayValue}</span>
        {value.length > 0 && !isAllSelected && (
          <FiX
            className="w-4 h-4 hover:text-red-400"
            onClick={handleClear}
          />
        )}
        <FiChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-full min-w-[180px] bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-64 overflow-y-auto">
          <div className="p-2">
            {/* All option */}
            <button
              onClick={handleSelectAll}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors
                ${isAllSelected ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                {isAllSelected && <FiCheck className="w-4 h-4" />}
              </span>
              <span>{allLabel}</span>
            </button>

            {/* Individual options */}
            {options.map((option) => {
              const isSelected = value.includes(option);
              return (
                <button
                  key={option}
                  onClick={() => handleToggle(option)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors
                    ${isSelected ? 'bg-blue-600/50 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                >
                  <span className="w-4 h-4 flex items-center justify-center border border-gray-500 rounded">
                    {isSelected && <FiCheck className="w-3 h-3" />}
                  </span>
                  <span className="truncate">{option}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
